/**
 * The Place search (CONTEXT.md) as the webapp runs it, ported from iOS
 * `PlaceSearchQuery`, `PlaceSearchScope` and `PlaceSearchStartList`: when
 * typing turns into a Google search, which destination a search is for, and
 * what it offers before anything is typed — the trip's own places ("In this
 * trip") and the destination's Suggestions ("Popular in Tokyo").
 *
 * Pure: no React, no Google SDK, so it tests in node.
 */

import { dayIndexOf, isValidCalendarDate } from './datetime'
import { GooglePlace, LocationLike, PlaceDraft, placeFromLocation, placePicked, placeTyped } from './place'

/** Two, not three: two characters of Japanese or Chinese can already name a place. */
const MIN_SEARCH_LENGTH = 2

/** How long typing must pause before a search goes out; every request is billed. */
export const PLACE_SEARCH_DEBOUNCE_MS = 400

/** Whether typed text is enough to search Google for — a single character is too vague to find anything. */
export const searchesGoogle = (text: string): boolean => [...text.trim()].length >= MIN_SEARCH_LENGTH

export interface LatLng {
	lat: number
	lng: number
}

/** The area typed searches lean toward — never restricted to, so the airport home and the day trip out are still found. */
export type SearchArea =
	| { kind: 'bounds'; northEast: LatLng; southWest: LatLng }
	| { kind: 'circle'; center: LatLng; radiusMeters: number }

/** A location as the API hands it back, with the place details the search reads. */
export interface SearchLocation extends LocationLike {
	placeDetails?: {
		geometry?: { location: LatLng; viewport?: { northeast: LatLng; southwest: LatLng } }
	} | null
}

/** What a Place search is for when it is for somewhere in particular: one Destination of the trip. */
export interface PlaceSearchScope {
	/** Names the Suggestions ("Popular in Tokyo"). */
	destinationName: string
	/** Trip places are listed nearest this first; null when the destination can't be placed on a map. */
	center: LatLng | null
	/** Null exactly when `center` is. */
	bias: SearchArea | null
	/**
	 * What Suggestions are looked up for, by name ("Tokyo, Japan") — which
	 * works for a country as well as a neighbourhood and needs no coordinates.
	 * Null when the search offers none.
	 */
	suggestionsPlaceName: string | null
}

/** The trip as a scope reads it: its dates and its destinations' days and places. */
export interface ScopeTrip {
	startDate: string | null
	dayCount: number
	destinations: { startDayIndex: number; dayCount: number; location: SearchLocation }[]
}

/** How far a destination without bounds is taken to reach. */
const BOUNDLESS_RADIUS_METERS = 25_000

const destinationOwning = (trip: ScopeTrip, dayIndex: number) =>
	trip.destinations.find(d => dayIndex >= d.startDayIndex && dayIndex < d.startDayIndex + d.dayCount) ??
	null

const scopeOf = (location: SearchLocation, offersSuggestions: boolean): PlaceSearchScope => {
	const geometry = location.placeDetails?.geometry
	const center = geometry?.location ?? null
	const viewport = geometry?.viewport
	return {
		destinationName: (location.primaryText ?? location.fullText).trim(),
		center,
		bias: viewport
			? { kind: 'bounds', northEast: viewport.northeast, southWest: viewport.southwest }
			: center && { kind: 'circle', center, radiusMeters: BOUNDLESS_RADIUS_METERS },
		suggestionsPlaceName: offersSuggestions ? location.fullText : null,
	}
}

/** An event's search: the destination that owns the event's day, with its Suggestions. */
export const eventSearchScope = (trip: ScopeTrip | undefined, dayIndex: number): PlaceSearchScope | null => {
	const destination = trip && destinationOwning(trip, dayIndex)
	return destination ? scopeOf(destination.location, true) : null
}

/** The trip day a `datetime-local` check-in falls on, or null on an undated trip or outside it. */
const dayIndexOfCheckIn = (trip: ScopeTrip, checkIn: string): number | null => {
	const date = checkIn.slice(0, 10)
	if (!isValidCalendarDate(date) || !isValidCalendarDate(trip.startDate)) return null
	const dayIndex = dayIndexOf(date, trip.startDate)
	return dayIndex >= 0 && dayIndex < trip.dayCount ? dayIndex : null
}

/**
 * An accommodation's search: the destination that owns the check-in day. When
 * there's no such day (no check-in yet, an undated trip, a check-in outside
 * the trip) only a trip with one destination can still say where. It leans
 * the search there, and never offers Suggestions.
 */
export const accommodationSearchScope = (
	trip: ScopeTrip | undefined,
	checkIn: string,
): PlaceSearchScope | null => {
	if (!trip) return null
	const checkInDay = dayIndexOfCheckIn(trip, checkIn)
	const destination =
		(checkInDay !== null && destinationOwning(trip, checkInDay)) ||
		(trip.destinations.length === 1 ? trip.destinations[0] : null)
	return destination ? scopeOf(destination.location, false) : null
}

/** Where Trip places come from: the trip's ideas and events, as the API lists them. */
export interface TripPlaceSources {
	ideas: { location: SearchLocation | null }[]
	events: { tripDayIndex: number; eventIndex: number; location: SearchLocation | null }[]
}

/** One key per place: its Google place ID, else its text — so a typed-in place used twice is one place. */
const placeKey = (location: SearchLocation): string =>
	location.googlePlaceId
		? `google:${location.googlePlaceId}`
		: `text:${location.fullText.trim().toLowerCase()}`

/**
 * The Trip places (CONTEXT.md): every place the trip's ideas and events are
 * at, each once — the ideas' first, then the events' in itinerary order.
 */
export const tripPlacesOf = ({ ideas, events }: TripPlaceSources): SearchLocation[] => {
	const inItineraryOrder = [...events].sort(
		(a, b) => a.tripDayIndex - b.tripDayIndex || a.eventIndex - b.eventIndex,
	)
	const seen = new Set<string>()
	return [...ideas, ...inItineraryOrder].flatMap(({ location }) => {
		if (!location) return []
		const key = placeKey(location)
		if (seen.has(key)) return []
		seen.add(key)
		return [location]
	})
}

/** A Suggestion (CONTEXT.md): one of Google's top sights for a destination. Never saved, unlike an idea. */
export interface PlaceSuggestion {
	placeId: string
	name: string
	address: string | null
	types: string[]
}

/**
 * What a Place search knows of its trip: the Trip places and Suggestions it
 * opens on, and the destination it is for, if any.
 */
export interface PlaceSearchContext {
	tripPlaces: SearchLocation[]
	suggestions: PlaceSuggestion[]
	scope: PlaceSearchScope | null
}

/** What a Place search is for: an event on a trip day, an idea, or a stay from its check-in. */
export type PlaceSearchFor =
	| { kind: 'event'; dayIndex: number }
	| { kind: 'idea' }
	/** `checkIn` as the form holds it: a `datetime-local` string, or '' when unset. */
	| { kind: 'accommodation'; checkIn: string }

/** The destination a search is for: an event's day's, a stay's check-in's, and none for an idea, which has no day. */
export const placeSearchScopeFor = (
	searchFor: PlaceSearchFor,
	trip: ScopeTrip | undefined,
): PlaceSearchScope | null => {
	switch (searchFor.kind) {
		case 'event':
			return eventSearchScope(trip, searchFor.dayIndex)
		case 'accommodation':
			return accommodationSearchScope(trip, searchFor.checkIn)
		case 'idea':
			return null
	}
}

/**
 * Everything a search knows of its trip, as iOS gives each search: an event's
 * opens on the Trip places and its destination's Suggestions, an idea's on the
 * Trip places alone, and an accommodation's only leans toward its destination.
 */
export const placeSearchContextOf = (
	searchFor: PlaceSearchFor,
	trip: ScopeTrip | undefined,
	sources: TripPlaceSources,
	suggestions: PlaceSuggestion[],
): PlaceSearchContext => {
	const scope = placeSearchScopeFor(searchFor, trip)
	return {
		scope,
		tripPlaces: searchFor.kind === 'accommodation' ? [] : tripPlacesOf(sources),
		suggestions: scope?.suggestionsPlaceName ? suggestions : [],
	}
}

/** One row of what the search opens on. "Show all" picks nothing; it opens up the rest of the Trip places. */
export type StartRow =
	| { kind: 'tripPlace'; location: SearchLocation }
	| { kind: 'showAllTripPlaces'; count: number }
	| { kind: 'suggestion'; suggestion: PlaceSuggestion }

export interface StartSection {
	title: string
	rows: StartRow[]
}

/** How many Trip places show before "Show all", so a long trip doesn't push the Suggestions out of sight. */
const COLLAPSED_TRIP_PLACE_COUNT = 5

const EARTH_RADIUS_METERS = 6_371_000

const toRadians = (degrees: number) => (degrees * Math.PI) / 180

/** Great-circle distance, which is all "nearest first" needs. */
const distanceMeters = (a: LatLng, b: LatLng): number => {
	const dLat = toRadians(b.lat - a.lat)
	const dLng = toRadians(b.lng - a.lng)
	const h =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(toRadians(a.lat)) * Math.cos(toRadians(b.lat)) * Math.sin(dLng / 2) ** 2
	return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(h))
}

/** Places without coordinates keep their order, after every placed one. */
const nearestFirst = (places: SearchLocation[], center: LatLng): SearchLocation[] =>
	places
		.map((location, index) => {
			const point = location.placeDetails?.geometry?.location
			return { location, index, distance: point ? distanceMeters(center, point) : Infinity }
		})
		.sort((a, b) => a.distance - b.distance || a.index - b.index)
		.map(({ location }) => location)

/**
 * What the Place search shows before anything is typed: the Trip places ("In
 * this trip"), nearest the destination first when it's on a map, then the
 * destination's Suggestions ("Popular in Tokyo") that aren't already in the
 * trip. A section with nothing in it is left out.
 */
export const placeSearchStart = ({
	tripPlaces,
	scope,
	suggestions,
	showsAllTripPlaces,
}: {
	tripPlaces: SearchLocation[]
	scope: PlaceSearchScope | null
	suggestions: PlaceSuggestion[]
	showsAllTripPlaces: boolean
}): StartSection[] => {
	const ordered = scope?.center ? nearestFirst(tripPlaces, scope.center) : tripPlaces
	const collapses = !showsAllTripPlaces && ordered.length > COLLAPSED_TRIP_PLACE_COUNT
	const tripRows: StartRow[] = (collapses ? ordered.slice(0, COLLAPSED_TRIP_PLACE_COUNT) : ordered).map(
		location => ({
			kind: 'tripPlace',
			location,
		}),
	)
	if (collapses) tripRows.push({ kind: 'showAllTripPlaces', count: ordered.length })

	const inTrip = new Set(tripPlaces.map(location => location.googlePlaceId).filter(Boolean))
	const suggestionRows: StartRow[] = suggestions
		.filter(candidate => !inTrip.has(candidate.placeId))
		.map(candidate => ({ kind: 'suggestion', suggestion: candidate }))

	const sections: StartSection[] = []
	if (tripRows.length > 0) sections.push({ title: 'In this trip', rows: tripRows })
	if (scope && suggestionRows.length > 0)
		sections.push({ title: `Popular in ${scope.destinationName}`, rows: suggestionRows })
	return sections
}

/** A Suggestion as the place choosing it saves: shaped like a search result's, "name, address". */
const googlePlaceOfSuggestion = ({ placeId, name, address, types }: PlaceSuggestion): GooglePlace => ({
	placeId,
	primaryText: name,
	secondaryText: address ?? '',
	fullText: address ? `${name}, ${address}` : name,
	types,
})

/** The place a row picks, as the forms hold one; null for "Show all", which picks nothing. */
export const placeOfRow = (row: StartRow): PlaceDraft | null => {
	switch (row.kind) {
		case 'suggestion':
			return placePicked(googlePlaceOfSuggestion(row.suggestion))
		case 'tripPlace': {
			const { googlePlace } = placeFromLocation(row.location)
			return googlePlace ? placePicked(googlePlace) : placeTyped(row.location.fullText)
		}
		case 'showAllTripPlaces':
			return null
	}
}
